import { useCallback, useState } from 'react';
import type { ClientEvent, InstructorState } from '@examatron/shared';
import * as api from '../lib/api';
import { useSocket } from '../lib/socket';
import { Distribution } from '../components/Distribution';
import '../styles/session.css';
import '../styles/admin.css';

/**
 * Instructor console for a running session — SPEC §4.2, §5.
 *
 * The control stack is ordered by how often it is used: Next and the two reveal
 * stages sit together and large. End Session is deliberately not among them; on
 * the system this replaces it sat one click below Next with no confirmation.
 */
export function Run({ sessionId }: { sessionId: string }) {
  const hello = useCallback((): ClientEvent | null => {
    const adminToken = api.loadToken();
    return adminToken ? { type: 'instructor:attach', sessionId, adminToken } : null;
  }, [sessionId]);

  const { state, connection, error, send } = useSocket<InstructorState>(hello);

  if (!api.loadToken()) {
    return (
      <div className="curtain">
        <div>
          <h2>Signed out</h2>
          <p>
            <a href="/admin" style={{ color: 'var(--accent)' }}>
              Sign in again
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="curtain">
        <div>
          <h2>{error ? 'Cannot open the session' : 'Connecting…'}</h2>
          {error && <p>{error}</p>}
        </div>
      </div>
    );
  }

  return <Console state={state} connection={connection} error={error} send={send} />;
}

function Console({
  state,
  connection,
  error,
  send,
}: {
  state: InstructorState;
  connection: string;
  error: string | null;
  send: (e: ClientEvent) => void;
}) {
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [endText, setEndText] = useState('');

  const q = state.question;
  const joinUrl = `${window.location.origin}/s/${state.code}`;
  const ended = state.status === 'COMPLETED';

  return (
    <div className="session">
      <div className="session__bar">
        <span>{state.sessionName}</span>
        <span className={`link link--${connection}`}>
          {state.progress.answered} of {state.progress.total} answered
        </span>
      </div>

      <div className="session__body" style={{ maxWidth: 1100 }}>
        {error && <div className="notice notice--bad">{error}</div>}

        {ended && (
          <div className="notice notice--good">
            This session has ended. Participants have been thanked.
          </div>
        )}

        <div className="run">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-5)' }}>
            {!q && (
              <div className="panel">
                <h2>Ready when you are</h2>
                <p className="panel__hint">
                  Participants join at <strong>{joinUrl}</strong>. Press Next Question to
                  begin — nothing appears on their screens until you do.
                </p>
              </div>
            )}

            {q && (
              <>
                <div>
                  <p className="qid tabular">{q.id}</p>
                  <p className="stem" style={{ marginTop: 'var(--s-3)' }}>
                    {q.stem}
                  </p>
                </div>

                <div className="options">
                  {q.options.map((o) => {
                    const isRight = state.correct?.includes(o.key) ?? false;
                    return (
                      <div
                        key={o.key}
                        className={`option${isRight ? ' option--right' : ''}`}
                        style={{ cursor: 'default' }}
                      >
                        <span className="option__key">{o.key}</span>
                        <span className="option__text">{o.text}</span>
                        {isRight && (
                          <span className="option__mark" aria-hidden="true">
                            ✓
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {state.distribution && state.correct && (
                  <Distribution
                    options={q.options.map((o) => o.key)}
                    distribution={state.distribution}
                    correct={state.correct}
                  />
                )}

                {/*
                  * Shown to the instructor from the moment the answer is
                  * revealed, which is when they start talking through it — a
                  * stage earlier than the room sees it. The banner makes the
                  * difference explicit, so nobody references something on their
                  * own screen that thirty people are not looking at.
                  */}
                {state.phase !== 'ANSWERING' && state.justification && (
                  <div className="explain">
                    {state.phase === 'ANSWER_SHOWN' && (
                      <p className="eyebrow" style={{ color: 'var(--miss)' }}>
                        Your eyes only — not yet on their screens
                      </p>
                    )}
                    <p>{state.justification}</p>
                    {state.whyNot && (
                      <div className="explain__why">
                        {Object.entries(state.whyNot).map(([key, text]) => (
                          <div key={key}>
                            <b>{key}.</b> {text}
                          </div>
                        ))}
                      </div>
                    )}
                    {state.referenceUrl && (
                      <a
                        href={state.referenceUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: 'var(--accent)', fontSize: 14 }}
                      >
                        AWS documentation →
                      </a>
                    )}
                  </div>
                )}
              </>
            )}

            {!ended && (
              <div className="controls">
                {state.phase === 'ANSWERING' && q && (
                  <button className="btn" onClick={() => send({ type: 'instructor:reveal-answer' })}>
                    {state.settings.autoRevealJustification
                      ? 'Reveal answer and justification'
                      : 'Reveal answer'}
                  </button>
                )}
                {/*
                  * The second stage is the step most easily missed — the room is
                  * looking at a revealed answer and nothing tells them the
                  * written rationale is one click away. Pulsing it says so.
                  */}
                {state.phase === 'ANSWER_SHOWN' && (
                  <button
                    className="btn btn--pending"
                    onClick={() => send({ type: 'instructor:reveal-justification' })}
                  >
                    Send justification to the room
                  </button>
                )}
                <button
                  className={state.phase === 'ANSWER_SHOWN' ? 'btn btn--quiet' : 'btn'}
                  onClick={() => send({ type: 'instructor:next' })}
                >
                  Next question
                </button>
                <button
                  className="btn btn--quiet"
                  disabled={state.askedIds.length < 2}
                  onClick={() => send({ type: 'instructor:previous' })}
                >
                  Previous
                </button>
                {state.status === 'PAUSED' ? (
                  <button className="btn btn--quiet" onClick={() => send({ type: 'instructor:resume' })}>
                    Resume
                  </button>
                ) : (
                  <button className="btn btn--quiet" onClick={() => send({ type: 'instructor:pause' })}>
                    Pause
                  </button>
                )}
              </div>
            )}
          </div>

          <aside className="side">
            <div className="code-plate">
              <p className="eyebrow">Join code</p>
              <p className="code-plate__code tabular">{state.code}</p>
              <p style={{ fontSize: 13, color: 'var(--slate)', marginTop: 'var(--s-2)' }}>
                {joinUrl.replace(/^https?:\/\//, '')}
              </p>
            </div>

            <div className="panel" style={{ padding: 'var(--s-4)' }}>
              <p className="eyebrow">Pace</p>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={state.settings.autoRevealJustification}
                  onChange={(e) =>
                    send({
                      type: 'instructor:settings',
                      settings: { autoRevealJustification: e.target.checked },
                    })
                  }
                />
                <span>
                  Reveal justification with the answer
                  <small>
                    Off means two clicks: correctness first, then the written rationale.
                  </small>
                </span>
              </label>
            </div>

            <div className="panel" style={{ padding: 'var(--s-4)' }}>
              <p className="eyebrow">Domains</p>
              <div className="domain-list">
                <button
                  className="domain-btn"
                  aria-pressed={state.scope.kind === 'ALL'}
                  onClick={() => send({ type: 'instructor:scope', scope: { kind: 'ALL' } })}
                >
                  <span>Everything</span>
                  <small className="tabular">
                    {state.askedIds.length}/{state.domains.reduce((n, d) => n + d.count, 0)}
                  </small>
                </button>
                {state.domains.map((d) => (
                  <button
                    key={d.number}
                    className="domain-btn"
                    aria-pressed={state.scope.kind === 'DOMAIN' && state.scope.domain === d.number}
                    onClick={() =>
                      send({ type: 'instructor:scope', scope: { kind: 'DOMAIN', domain: d.number } })
                    }
                  >
                    <span>
                      D{String(d.number).padStart(2, '0')} · {d.name}
                    </span>
                    <small className="tabular">
                      {d.asked}/{d.count}
                    </small>
                  </button>
                ))}
              </div>
            </div>

            <div className="panel" style={{ padding: 'var(--s-4)' }}>
              <p className="eyebrow">Room · {state.roster.length}</p>
              <div className="roster">
                {state.roster.length === 0 && (
                  <p className="panel__hint">Nobody has joined yet.</p>
                )}
                {state.roster.map((p) => (
                  <div className="roster__row" key={p.participantId} data-off={!p.connected}>
                    <span>{p.name}</span>
                    {/* Whether, never what — the wire does not carry the choice. */}
                    <span style={{ color: p.answered ? 'var(--accent)' : 'var(--slate)' }}>
                      {p.answered ? 'answered' : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {!ended && (
              <div className="panel" style={{ padding: 'var(--s-4)' }}>
                {!confirmEnd ? (
                  <button className="btn btn--quiet" onClick={() => setConfirmEnd(true)}>
                    End session
                  </button>
                ) : (
                  <div className="field">
                    <label htmlFor="endconfirm">Type END to confirm</label>
                    <input
                      id="endconfirm"
                      autoFocus
                      value={endText}
                      onChange={(e) => setEndText(e.target.value.toUpperCase())}
                    />
                    <div style={{ display: 'flex', gap: 'var(--s-2)' }}>
                      <button
                        className="btn"
                        disabled={endText !== 'END'}
                        onClick={() => send({ type: 'instructor:end' })}
                      >
                        End
                      </button>
                      <button
                        className="btn btn--quiet"
                        onClick={() => {
                          setConfirmEnd(false);
                          setEndText('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
